/**
 * FMV picker — sub-modal (rendered into #pickerRoot so it stacks above an
 * underlying edit modal). Shows the candidates from fmvLookupForX, lets the
 * user pick a tile or enter a unit price manually, and calls `onApply(unit)`.
 *
 * Plus the three entry points wired to inline handlers:
 *   • refreshSealedFMV / refreshCardFMV — direct table-row refresh; commits to DB.
 *   • refreshFMVInForm — invoked from inside Sealed/Card edit forms; writes the
 *     unit price into the form's Current FMV input only (Save still required).
 */
import { DB } from "../lib/db";
import { escapeHtml, toast, $ } from "../lib/dom";
import { fmt$ } from "../utils/format";
import { fmvLookupForCard, fmvLookupForSealed, isGradedCard, isFmvLocked, type FmvCandidate } from "../lib/fmv";
import { fmvFromImageUrl } from "../lib/fmvByImage";
import { readForm } from "./formField";
import { markModalDirty } from "./modal";
import { render } from "../state";

/** `manual` is true when the user typed the price (vs. picking an API/catalog
 *  candidate). Callers use it to set/clear the per-lot FMV lock. */
type ApplyFn = (unit: number, manual: boolean) => void;

interface PickerState {
  candidates: FmvCandidate[];
  qty: number;
  currentFMV: any;
  onApply: ApplyFn;
}

let _state: PickerState | null = null;

function closeFMVPicker(): void {
  document.getElementById("pickerRoot") && (document.getElementById("pickerRoot")!.innerHTML = "");
  _state = null;
}

function applyFMVPickerManual(): void {
  const state = _state;
  if (!state) return;
  const inp = document.getElementById("fmvPickerManual") as HTMLInputElement | null;
  const val = inp ? Number(inp.value) : NaN;
  if (!isFinite(val) || val < 0) { toast("Enter a valid number.", "err"); return; }
  const unitPrice = +val.toFixed(2);
  closeFMVPicker();
  state.onApply(unitPrice, true);
  toast(`Unit FMV set to ${fmt$(unitPrice)} (manual)`);
}

interface ShowPickerArgs {
  title: string;
  subtitle?: string;
  hint?: string;
  candidates: FmvCandidate[];
  externalUrl?: string;
  externalLabel?: string;
  qty: number;
  currentFMV: any;
  onApply: ApplyFn;
}

function showFMVPicker(args: ShowPickerArgs): void {
  const { title, subtitle, hint, candidates, externalUrl, externalLabel, qty, currentFMV, onApply } = args;
  let root = document.getElementById("pickerRoot");
  if (!root) { root = document.createElement("div"); root.id = "pickerRoot"; document.body.appendChild(root); }
  const curStr = (currentFMV != null && currentFMV !== "") ? fmt$(currentFMV) : "(blank)";

  root.innerHTML = `
    <div class="fixed inset-0 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto" style="z-index:55">
      <div class="panel w-full max-w-5xl my-6 shadow-2xl">
        <div class="flex items-start justify-between p-4 border-b border-[var(--border)] gap-3">
          <div class="min-w-0">
            <h3 class="font-semibold">${escapeHtml(title)}</h3>
            ${subtitle ? `<div class="text-xs text-[var(--muted)] mt-1 truncate">${escapeHtml(subtitle)}</div>` : ""}
            <div class="text-xs text-[var(--muted)] mt-1">Current FMV (per unit): <b>${curStr}</b> · Qty <b>${qty}</b> · click an option to set the unit FMV; total = unit × qty</div>
            ${hint ? `<div class="text-xs text-amber-300 mt-2">${escapeHtml(hint)}</div>` : ""}
          </div>
          <button class="btn btn-ghost !py-1 !px-2 shrink-0" id="_fmvClose" title="Close">✕</button>
        </div>
        <div class="p-4 overflow-y-auto" style="max-height:60vh">
          ${candidates.length ?
            `<div class="grid-cards" id="fmvPickerGrid">
              ${candidates.map((c, i) => `
                <div class="panel-2 p-3 clickable" data-pick-idx="${i}" style="border-color:var(--border); transition:all .15s">
                  ${c.image ? `<img src="${escapeHtml(c.image)}" loading="lazy" class="rounded mb-2 mx-auto block" style="max-height:200px; width:auto;" alt="">` : `<div class="rounded mb-2 mx-auto bg-[var(--panel)] grid place-items-center text-xs text-[var(--muted)]" style="height:160px">No image</div>`}
                  <div class="font-semibold text-sm leading-tight">${escapeHtml(c.name || "")}</div>
                  <div class="text-xs text-[var(--muted)] mt-0.5 truncate">${escapeHtml(c.set || "")}${c.number ? ' · #' + escapeHtml(c.number) : ""}</div>
                  ${c.variant ? `<div class="text-xs text-cyan-300 mt-1">${escapeHtml(c.variant)}</div>` : ""}
                  <div class="flex justify-between items-baseline mt-1">
                    <span class="text-xs text-[var(--muted)]">unit</span>
                    <span class="text-emerald-300 font-bold">${fmt$(c.unit)}</span>
                  </div>
                  <div class="flex justify-between items-baseline">
                    <span class="text-xs text-[var(--muted)]">× ${qty} = total</span>
                    <span class="text-xs font-semibold">${fmt$(+(c.unit * qty).toFixed(2))}</span>
                  </div>
                </div>
              `).join("")}
            </div>` :
            `<div class="text-sm text-[var(--muted)] text-center py-6">No automatic matches found. Use the manual entry below or open the external search.</div>`
          }
        </div>
        <div class="p-4 border-t border-[var(--border)] space-y-3">
          <div class="text-sm text-[var(--muted)]">Don't see the right one?</div>
          <div class="flex flex-wrap gap-2 items-end">
            ${externalUrl ? `<button type="button" class="btn btn-ghost shrink-0" id="_fmvExt">Open ${escapeHtml(externalLabel || "external search")} ↗</button>` : ""}
            <div class="flex-1 min-w-[180px]">
              <label class="label">Enter unit price manually ($)</label>
              <input type="number" step="0.01" class="input" id="fmvPickerManual" placeholder="e.g. 12.50">
            </div>
            <button type="button" class="btn btn-primary" id="_fmvApplyManual">Apply manual</button>
          </div>
          <div class="flex justify-end pt-1">
            <button class="btn btn-ghost" id="_fmvCancel">Cancel</button>
          </div>
        </div>
      </div>
    </div>`;

  _state = { candidates, qty, currentFMV, onApply };

  // Wire grid clicks
  document.getElementById("fmvPickerGrid")?.addEventListener("click", (e) => {
    const tile = (e.target as HTMLElement).closest<HTMLElement>("[data-pick-idx]");
    if (!tile) return;
    const idx = Number(tile.dataset.pickIdx);
    const c = candidates[idx];
    if (!c) return;
    const unitPrice = +Number(c.unit).toFixed(2);
    closeFMVPicker();
    onApply(unitPrice, false);
    toast(`Unit FMV set to ${fmt$(unitPrice)} (${c.variant || "matched"})`);
  });
  document.getElementById("_fmvClose")?.addEventListener("click", closeFMVPicker);
  document.getElementById("_fmvCancel")?.addEventListener("click", closeFMVPicker);
  document.getElementById("_fmvApplyManual")?.addEventListener("click", applyFMVPickerManual);
  document.getElementById("fmvPickerManual")?.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") { e.preventDefault(); applyFMVPickerManual(); }
  });
  if (externalUrl) {
    document.getElementById("_fmvExt")?.addEventListener("click",
      () => window.open(externalUrl, "_blank", "noopener"));
  }
  // Focus the manual-entry input if there are no candidates
  setTimeout(() => {
    if (!candidates.length) (document.getElementById("fmvPickerManual") as HTMLInputElement | null)?.focus();
  }, 50);
}

async function refreshFMV(
  scope: "sealed" | "card",
  row: Record<string, any>,
  applyFn: ApplyFn,
): Promise<void> {
  toast("Looking up price…");
  let result;
  try {
    result = scope === "card" ? await fmvLookupForCard(row) : await fmvLookupForSealed(row);
  } catch (e: any) {
    toast("Lookup failed: " + (e?.message || e), "err");
    return;
  }
  if (!result)       { toast("No price found.", "err"); return; }
  if (result.error)  { toast(result.error, "err"); return; }

  const qty = Number(row["Qty"]) || 1;
  const itemName = scope === "card" ? row["Card Name"] : row["Product"];
  const setName = row["Set"] || "";

  showFMVPicker({
    title: "Pick the matching item",
    subtitle: `${itemName || ""}${setName ? ` · ${setName}` : ""}`,
    hint: result.hint,
    candidates: result.candidates || [],
    externalUrl: result.external,
    externalLabel: result.externalLabel,
    qty,
    currentFMV: row["Current FMV"],
    onApply: applyFn,
  });
}

/* ============================================================
   Row-level FMV refresh — commits straight to DB.
   The form stores per-LOT total FMV; the picker yields per-unit.
   ============================================================ */
export function refreshSealedFMV(id: string): void {
  const s = DB.table("sealed").find(r => r["Lot ID"] === id);
  if (!s) { toast("Lot not found", "err"); return; }
  const qtyRaw = Number(s["Qty"]);
  const qty = Number.isFinite(qtyRaw) ? qtyRaw : 1; // missing -> 1, but explicit 0 stays 0
  if (qty <= 0) {
    DB.update("sealed", "Lot ID", id, { "Current FMV": 0, "Unrealized G/L": -(Number(s["Total Basis"]) || 0) });
    toast("Lot has 0 qty — Current FMV set to $0.");
    render();
    return;
  }
  if (isFmvLocked(s)) toast("This lot's FMV is locked (manual). Picking a market price will unlock it; Apply manual keeps the lock.");
  const sCopy = { ...s, "Current FMV": qty > 0 ? (Number(s["Current FMV"]) || 0) / qty : 0 };
  refreshFMV("sealed", sCopy, (unitFMV, manual) => {
    const totalFMV = +(unitFMV * qty).toFixed(2);
    const basis = Number(s["Total Basis"]) || 0;
    DB.update("sealed", "Lot ID", id, {
      "Current FMV": totalFMV,
      "Unrealized G/L": +(totalFMV - basis).toFixed(2),
      "Valuation Source": manual ? "Manual" : "TCGplayer Market",
      "FMV Locked": manual,
    });
    toast(`Total FMV updated to ${fmt$(totalFMV)}${manual ? " · locked (manual)" : ""}`);
    render();
  });
}

export function refreshCardFMV(id: string): void {
  const c = DB.table("cards").find(r => r["Card Lot ID"] === id);
  if (!c) { toast("Card not found", "err"); return; }
  const qtyRaw = Number(c["Qty"]);
  const qty = Number.isFinite(qtyRaw) ? qtyRaw : 1; // missing -> 1, but explicit 0 stays 0
  if (qty <= 0) {
    // Sold/traded-out lot has no inventory value — force FMV to 0 rather than
    // pricing a phantom unit (which inflated totals).
    DB.update("cards", "Card Lot ID", id, { "Current FMV": 0, "Unrealized G/L": -(Number(c["Total Basis"]) || 0) });
    toast("Lot has 0 qty — Current FMV set to $0.");
    render();
    return;
  }

  const graded = isGradedCard(c);

  // Open the picker. For RAW cards this shows API/image candidates; for GRADED
  // slabs fmvLookupForCard returns no candidates plus an eBay sold-listings link
  // and a manual-entry box — raw-card prices don't apply to slabs.
  const runPicker = () => {
    const cCopy = { ...c, "Current FMV": qty > 0 ? (Number(c["Current FMV"]) || 0) / qty : 0 };
    refreshFMV("card", cCopy, (unitFMV, manual) => {
      const totalFMV = +(unitFMV * qty).toFixed(2);
      const basis = Number(c["Total Basis"]) || 0;
      DB.update("cards", "Card Lot ID", id, {
        "Current FMV": totalFMV,
        "Unrealized G/L": +(totalFMV - basis).toFixed(2),
        "Valuation Source": manual ? (graded ? "Manual (graded)" : "Manual") : (c["Game"] === "Magic: The Gathering" ? "Scryfall" : "TCGPlayer"),
        "FMV Locked": manual,
      });
      toast(`Total FMV updated to ${fmt$(totalFMV)}${manual ? " · locked (manual)" : ""}`);
      render();
    });
  };

  // Graded slabs must be valued manually — skip the raw-price fast path.
  if (graded) { runPicker(); return; }

  // Locked lot: never silently overwrite via the image fast path. Explicit
  // refresh opens the picker so the user decides (manual keeps lock, market
  // candidate clears it).
  if (isFmvLocked(c)) {
    toast("This lot's FMV is locked (manual). Picking a market price will unlock it; Apply manual keeps the lock.");
    runPicker();
    return;
  }

  // Fast path (raw cards only): Image URL → exact card ID → current price.
  // No picker needed; one-click refresh. Falls back to the picker on a miss.
  toast("Looking up price…");
  (async () => {
    const result = await fmvFromImageUrl(c);
    if (!result) return false;
    const unitFMV = result.unit;
    const totalFMV = +(unitFMV * qty).toFixed(2);
    const basis = Number(c["Total Basis"]) || 0;
    DB.update("cards", "Card Lot ID", id, {
      "Current FMV": totalFMV,
      "Unrealized G/L": +(totalFMV - basis).toFixed(2),
      "Valuation Source": (c["Game"] === "Magic: The Gathering" ? "Scryfall" : "TCGPlayer"),
    });
    toast(`Total FMV updated to ${fmt$(totalFMV)} via image match · ${result.variant}`);
    render();
    return true;
  })().then(ok => { if (!ok) runPicker(); });
}

/** In-form variant. Writes the unit FMV to the form's input; Save still required. */
export function refreshFMVInForm(scope: "sealed" | "card"): void {
  const formId = scope === "sealed" ? "sealedForm" : "cardForm";
  const form = $("#" + formId) as HTMLFormElement | null;
  if (!form) return;
  const row = readForm(form);

  const writeUnit = (unitFMV: number, msg: string, manual: boolean) => {
    const fmvInput = form.querySelector<HTMLInputElement>('[name="Current FMV"]');
    if (fmvInput) {
      fmvInput.value = String(unitFMV);
      // Mirror the lock semantics into the form's checkbox: manual entry
      // locks, picking a market candidate unlocks. Committed on Save.
      const lockCb = form.querySelector<HTMLInputElement>('[name="FMV Locked"]');
      if (lockCb) lockCb.checked = manual;
      markModalDirty();
      toast(msg);
    }
  };

  // Card scope: try image-URL fast path first.
  if (scope === "card") {
    // Graded slabs are valued manually — skip the raw-price fast path and open
    // the picker (no auto candidates; eBay link + manual entry box).
    if (isGradedCard(row)) {
      refreshFMV(scope, row, (unitFMV, manual) => writeUnit(unitFMV, "Unit FMV filled (manual, graded) — click Save to commit", manual));
      return;
    }
    toast("Looking up price…");
    fmvFromImageUrl(row).then(result => {
      if (result) {
        writeUnit(result.unit, `Unit FMV filled via image match · ${result.variant} — click Save to commit`, false);
        return;
      }
      refreshFMV(scope, row, (unitFMV, manual) => writeUnit(unitFMV, "Unit FMV filled — click Save to commit", manual));
    });
    return;
  }
  // Sealed scope: existing fuzzy flow only.
  refreshFMV(scope, row, (unitFMV, manual) => writeUnit(unitFMV, "Unit FMV filled — click Save to commit", manual));
}

// Inline-handler bridges
(window as any).refreshSealedFMV  = refreshSealedFMV;
(window as any).refreshCardFMV    = refreshCardFMV;
(window as any).refreshFMVInForm  = refreshFMVInForm;
